export interface UserPreferences {
    /** Current role name, empty when the user has none. */
    role: string;
    /** True when the user has no role or only the disabled default role, and should pick one. */
    needsRoleSelection: boolean;
    emailNotificationsEnabled: boolean;
}

/** Fields left out (or null) are not changed. */
export interface UserPreferencesUpdate {
    roleName?: string | null;
    emailNotificationsEnabled?: boolean | null;
}

export type UpdateUserPreferencesResult =
    | { ok: true; preferences: UserPreferences }
    | { ok: false; error: string };

// Pages where a role prompt would be out of place, e.g. the Teams tab configuration page.
const ONBOARDING_EXCLUDED_ROUTES = ["/privacy", "/termsofuse", "/config"];

const SAVE_FAILED_MESSAGE = "Your settings could not be saved. Please try again.";

const preferencesUrl = (userId: string): string =>
    `${process.env.REACT_APP_API_URL}/users/${encodeURIComponent(userId)}/preferences`;

export const isSameRole = (first: string, second: string): boolean =>
    first.trim().toLowerCase() === second.trim().toLowerCase();

export const toUserPreferences = (value: unknown): UserPreferences | null => {
    if (!value || typeof value !== "object") {
        return null;
    }

    const { role, needsRoleSelection, emailNotificationsEnabled } = value as Record<string, unknown>;
    if (typeof needsRoleSelection !== "boolean" || typeof emailNotificationsEnabled !== "boolean") {
        return null;
    }
    if (role !== null && role !== undefined && typeof role !== "string") {
        return null;
    }

    return { role: typeof role === "string" ? role : "", needsRoleSelection, emailNotificationsEnabled };
};

/** Enabled role names, as listed by the API. Empty when they cannot be loaded. */
export async function GetAvailableRoles(): Promise<string[]> {
    try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/roles`, { method: "GET" });
        if (!response.ok) {
            console.warn(`Failed to load roles. Status: ${response.status}`);
            return [];
        }

        const roles = await response.json();
        return Array.isArray(roles)
            ? roles.filter((role): role is string => typeof role === "string" && role.trim() !== "")
            : [];
    } catch (error) {
        console.error("Failed to load roles", error);
        return [];
    }
}

export async function GetUserPreferences(userId: string): Promise<UserPreferences | null> {
    try {
        const response = await fetch(preferencesUrl(userId), { method: "GET" });
        if (!response.ok) {
            console.warn(`Failed to load user preferences. Status: ${response.status}`);
            return null;
        }

        const preferences = toUserPreferences(await response.json());
        if (!preferences) {
            console.error("Unexpected user preferences response.");
        }
        return preferences;
    } catch (error) {
        console.error("Failed to load user preferences", error);
        return null;
    }
}

export async function UpdateUserPreferences(userId: string, update: UserPreferencesUpdate): Promise<UpdateUserPreferencesResult> {
    try {
        const response = await fetch(preferencesUrl(userId), {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(update),
        });

        if (!response.ok) {
            const serverMessage = response.status === 400 ? await readErrorMessage(response) : null;
            console.warn(`Failed to save user preferences. Status: ${response.status}`);
            return { ok: false, error: serverMessage ?? errorMessageForStatus(response.status) };
        }

        const preferences = toUserPreferences(await response.json());
        if (!preferences) {
            console.error("Unexpected user preferences response.");
            return { ok: false, error: SAVE_FAILED_MESSAGE };
        }
        return { ok: true, preferences };
    } catch (error) {
        console.error("Failed to save user preferences", error);
        return { ok: false, error: SAVE_FAILED_MESSAGE };
    }
}

const errorMessageForStatus = (status: number): string => {
    if (status === 400) {
        return "The selected settings are not valid.";
    }
    if (status === 404) {
        return "Your user account could not be found.";
    }
    return SAVE_FAILED_MESSAGE;
};

// Handles plain strings, { message | error | detail | title } objects and ValidationProblem bodies.
const readErrorMessage = async (response: Response): Promise<string | null> => {
    let text: string;
    try {
        text = (await response.text()).trim();
    } catch {
        return null;
    }
    if (!text) {
        return null;
    }

    try {
        return messageFromBody(JSON.parse(text));
    } catch {
        return text.length <= 300 && !text.startsWith("<") ? text : null;
    }
};

const messageFromBody = (body: unknown): string | null => {
    if (typeof body === "string") {
        return body.trim() || null;
    }
    if (!body || typeof body !== "object") {
        return null;
    }

    const record = body as Record<string, unknown>;
    if (record.errors && typeof record.errors === "object") {
        const messages = Object.values(record.errors as Record<string, unknown>)
            .flatMap((value) => (Array.isArray(value) ? value : [value]))
            .filter((message): message is string => typeof message === "string" && message.trim() !== "");
        if (messages.length > 0) {
            return messages.join(" ");
        }
    }

    for (const key of ["message", "error", "detail", "title"]) {
        const value = record[key];
        if (typeof value === "string" && value.trim()) {
            return value.trim();
        }
    }
    return null;
};

const normalizeRoute = (route: string): string => {
    const path = route.replace(/^#/, "").split(/[?#]/)[0].replace(/\/+$/, "").toLowerCase();
    return path.startsWith("/") ? path : `/${path}`;
};

/** Whether the role onboarding prompt should be shown for the given (hash router) route. */
export const needsRoleOnboarding = (preferences: UserPreferences | null, roles: string[], route: string): boolean => {
    if (!preferences || !preferences.needsRoleSelection || roles.length === 0) {
        return false;
    }

    const path = normalizeRoute(route);
    return !ONBOARDING_EXCLUDED_ROUTES.some((excluded) => path === excluded || path.startsWith(`${excluded}/`));
};
